import React, { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';

type GraphNodeData = {
  id: string;
  label: string;
  type: 'Document' | 'Entity' | 'Chunk';
  entityType?: string;
  properties?: Record<string, string>;
  color?: string;
};

type GraphEdgeData = {
  source: string;
  target: string;
  label: string;
  context?: string;
};

type GraphCanvasProps = {
  nodes: GraphNodeData[];
  edges: GraphEdgeData[];
  selectedNodeId: string | null;
  highlightedNodeId: string | null;
  onNodeClick: (node: GraphNodeData) => void;
  onNodeHover: (node: GraphNodeData | null) => void;
  onBackgroundClick: () => void;
};

const PALETTE = ['#ef4444', '#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'];
const typeColorMap = new Map<string, string>();

const getNodeColor = (node: GraphNodeData): string => {
  if (node.color) return node.color;
  const key = node.entityType || node.type;
  if (!typeColorMap.has(key)) {
    typeColorMap.set(key, PALETTE[typeColorMap.size % PALETTE.length]);
  }
  return typeColorMap.get(key) || '#94a3b8';
};

export const GraphCanvas: React.FC<GraphCanvasProps> = ({
  nodes,
  edges,
  selectedNodeId,
  highlightedNodeId,
  onNodeClick,
  onNodeHover,
  onBackgroundClick,
}) => {
  const fgRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 500 });
  const hasFitRef = useRef(false);

  // Measure container on resize
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setDimensions({ width: Math.floor(width), height: Math.floor(height) });
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Crank up repulsion & link distance after graph mounts
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    fg.d3Force('charge')?.strength(-1200);
    fg.d3Force('link')?.distance(200);
    fg.d3Force('collision')?.radius(40);
  });

  // Reset fit-once flag when the node set changes
  useEffect(() => {
    hasFitRef.current = false;
  }, [nodes.map((n) => n.id).join(',')]);

  const handleZoomIn  = () => fgRef.current?.zoom(fgRef.current.zoom() * 1.4, 200);
  const handleZoomOut = () => fgRef.current?.zoom(fgRef.current.zoom() / 1.4, 200);
  const handleFit     = () => fgRef.current?.zoomToFit(300, 60);

  const graphData = useMemo(() => ({
    nodes: nodes.map((n) => ({ ...n })),
    links: edges.map((e) => ({ ...e })),
  }), [
    nodes.map((n) => n.id).join(','),
    edges.map((e) => `${e.source}-${e.target}`).join(','),
  ]);

  const nodeCanvasObject = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const label = node.label || node.id;
    const isSelected    = node.id === selectedNodeId;
    const isHighlighted = node.id === highlightedNodeId;
    const color  = getNodeColor(node);
    // Scale radius up when zoomed out so nodes remain readable; cap to avoid overflow
    const baseRadius = isSelected ? 28 : isHighlighted ? 24 : 20;
    const radius = Math.min(Math.max(baseRadius, 18 / globalScale), 80);

    // Outer glow ring for selected/highlighted
    if (isSelected || isHighlighted) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius + 6, 0, 2 * Math.PI);
      ctx.fillStyle = isSelected ? 'rgba(99,102,241,0.25)' : 'rgba(148,163,184,0.18)';
      ctx.fill();
    }

    // Inner ring (border)
    ctx.beginPath();
    ctx.arc(node.x, node.y, radius + 2.5, 0, 2 * Math.PI);
    ctx.fillStyle = isSelected ? '#6366f1' : isHighlighted ? '#94a3b8' : 'rgba(255,255,255,0.15)';
    ctx.fill();

    // Main circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();

    // Only render labels when zoomed in enough to read them
    if (globalScale < 0.2) return;

    const fontSize = Math.max(10, 13 / globalScale);
    ctx.font = `${isSelected ? '700' : '500'} ${fontSize}px Inter, Sans-Serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    const maxLen = 24;
    const displayLabel = label.length > maxLen ? label.substring(0, maxLen) + '…' : label;

    // Dark pill background for readability on dark canvas
    const textWidth = ctx.measureText(displayLabel).width;
    const padX = 5, padY = 2;
    const bx = node.x - textWidth / 2 - padX;
    const by = node.y + radius + 5;
    ctx.fillStyle = 'rgba(15,23,42,0.82)';
    ctx.strokeStyle = 'rgba(99,102,241,0.3)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.roundRect(bx, by, textWidth + padX * 2, fontSize + padY * 2, 4);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = isSelected ? '#a5b4fc' : '#e2e8f0';
    ctx.fillText(displayLabel, node.x, by + padY);
  }, [selectedNodeId, highlightedNodeId]);

  const nodePointerAreaPaint = useCallback((node: any, color: string, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const baseRadius = node.id === selectedNodeId ? 28 : node.id === highlightedNodeId ? 24 : 20;
    const radius = Math.min(Math.max(baseRadius, 18 / globalScale), 80);
    ctx.beginPath();
    ctx.arc(node.x, node.y, radius + 4, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  }, [selectedNodeId, highlightedNodeId]);

  // Only draw edge label when one of its endpoints is selected/highlighted
  const linkCanvasObject = useCallback((link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const start = link.source;
    const end   = link.target;
    if (!start || !end || typeof start.x !== 'number') return;

    const isActive = start.id === selectedNodeId || end.id === selectedNodeId
                  || start.id === highlightedNodeId || end.id === highlightedNodeId;

    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.strokeStyle = isActive ? '#818cf8' : 'rgba(148,163,184,0.35)';
    ctx.lineWidth = isActive ? 2.5 : 1.5;
    ctx.stroke();

    // Edge label: only on active edges and only when zoomed in enough
    if (isActive && link.label && globalScale > 0.45) {
      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;
      const fontSize = Math.max(8, 10 / globalScale);
      ctx.font = `500 ${fontSize}px Inter, Sans-Serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const w = ctx.measureText(link.label).width + 8;
      ctx.fillStyle = 'rgba(15,23,42,0.88)';
      ctx.strokeStyle = 'rgba(99,102,241,0.4)';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.roundRect(midX - w / 2, midY - fontSize / 2 - 2, w, fontSize + 4, 3);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#c7d2fe';
      ctx.fillText(link.label, midX, midY);
    }
  }, [selectedNodeId, highlightedNodeId]);

  return (
    <div
      ref={containerRef}
      className="flex-1 relative min-h-0"
      style={{ minHeight: 400, background: '#0f172a' }}
    >
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        nodeId="id"
        nodeCanvasObject={nodeCanvasObject}
        nodePointerAreaPaint={nodePointerAreaPaint}
        linkCanvasObject={linkCanvasObject}
        onNodeClick={(node: any) => onNodeClick(node)}
        onBackgroundClick={() => onBackgroundClick()}
        onNodeHover={(node: any) => onNodeHover(node || null)}
        onNodeDrag={(node: any) => onNodeHover(node)}
        onNodeDragEnd={(node: any) => {
          // Fix node position after drag so it stays put
          node.fx = node.x;
          node.fy = node.y;
          onNodeHover(null);
        }}
        onEngineStop={() => {
          if (!hasFitRef.current) {
            hasFitRef.current = true;
            fgRef.current?.zoomToFit(400, 60);
          }
        }}
        enableNodeDrag={true}
        enableZoomInteraction={true}
        enablePanInteraction={true}
        backgroundColor="#0f172a"
        linkDirectionalArrowLength={8}
        linkDirectionalArrowRelPos={0.88}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.3}
        width={dimensions.width}
        height={dimensions.height}
      />

      {/* Zoom controls */}
      <div className="absolute bottom-3 right-3 flex flex-col gap-1 z-10">
        <button onClick={handleFit} title="Fit all"
          className="w-8 h-8 flex items-center justify-center bg-slate-800 border border-slate-600 rounded shadow-sm hover:bg-slate-700 text-slate-300">
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={handleZoomIn} title="Zoom in"
          className="w-8 h-8 flex items-center justify-center bg-slate-800 border border-slate-600 rounded shadow-sm hover:bg-slate-700 text-slate-300">
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <button onClick={handleZoomOut} title="Zoom out"
          className="w-8 h-8 flex items-center justify-center bg-slate-800 border border-slate-600 rounded shadow-sm hover:bg-slate-700 text-slate-300">
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
